import React, { useState, useMemo } from 'react';
import { 
  ArrayInput, 
  SimpleFormIterator, 
  FormDataConsumer 
} from 'react-admin';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  SxProps,
  Theme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * Props for LazyAccordionIterator component
 */
export interface LazyAccordionIteratorProps {
  /** The source field for the array input (e.g., "configuration.relationships") */
  source: string;
  
  /** Label for the array input */
  label?: string;
  
  /** Function to render the accordion summary (collapsed view) */
  renderSummary: (item: any, index: number, formData: any) => React.ReactNode;
  
  /** Function to render the accordion details (expanded form) */
  renderDetails: (item: any, index: number, formData: any, getSource: any) => React.ReactNode;
  
  /** Optional function to generate unique ID for each item (defaults to item.id or index) */
  getItemId?: (item: any, index: number) => string;
  
  /** Optional styles for the accordion container */
  accordionSx?: SxProps<Theme>;
  
  /** Optional styles for the accordion details (form area) */
  detailsSx?: SxProps<Theme>;
  
  /** Allow reordering items (default: false) */
  disableReordering?: boolean;
  
  /** Allow adding new items (default: true) */
  disableAdd?: boolean;
  
  /** Allow removing items (default: true) */
  disableRemove?: boolean;
}

/**
 * LazyAccordionIterator - A reusable component for rendering array inputs as accordions
 * with lazy-loaded form content for optimal performance.
 * 
 * Features:
 * - Renders items as collapsible accordions
 * - Only renders form fields when accordion is expanded (lazy loading)
 * - Fully customizable summary and details rendering via render props
 * - Manages expansion state internally
 * - Integrates seamlessly with React Admin's form system
 * 
 * @example
 * ```tsx
 * <LazyAccordionIterator
 *   source="relationships"
 *   label="Schema Relationships"
 *   renderSummary={(item) => (
 *     <Box>
 *       <Typography>{item.name}</Typography>
 *       <Chip label={item.type} />
 *     </Box>
 *   )}
 *   renderDetails={(item, index, formData, getSource) => (
 *     <Grid container spacing={2}>
 *       <Grid item xs={12}>
 *         <TextInput source={getSource('name')} label="Name" fullWidth />
 *       </Grid>
 *       <Grid item xs={6}>
 *         <SelectInput source={getSource('type')} choices={typeChoices} fullWidth />
 *       </Grid>
 *     </Grid>
 *   )}
 * />
 * ```
 */
export const LazyAccordionIterator: React.FC<LazyAccordionIteratorProps> = ({
  source,
  label,
  renderSummary,
  renderDetails,
  getItemId,
  accordionSx = {},
  detailsSx = {},
  disableReordering = true,
  disableAdd = false,
  disableRemove = false
}) => {
  // Track which accordions are expanded for lazy rendering
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(new Set());

  // Toggle accordion expansion
  const toggleAccordion = (accordionId: string) => {
    setExpandedAccordions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accordionId)) {
        newSet.delete(accordionId);
      } else {
        newSet.add(accordionId);
      }
      return newSet;
    });
  };

  // Default accordion styles with performance optimizations
  const defaultAccordionSx: SxProps<Theme> = useMemo(() => ({
    width: '100%',
    mb: 1,
    '& .RaSimpleFormIterator-line': {
      maxHeight: 'none !important',
      height: 'auto !important',
      overflow: 'visible !important'
    },
    '& .RaSimpleFormIterator-root': {
      maxHeight: 'none !important',
      overflow: 'visible !important'
    },
    '& .MuiAccordion-root': {
      maxHeight: 'none !important',
      overflow: 'visible !important'
    },
    '& .MuiCollapse-root': {
      maxHeight: 'none !important',
      overflow: 'visible !important'
    },
    '& .MuiCollapse-wrapper': {
      maxHeight: 'none !important',
      minHeight: '0 !important'
    },
    '& .MuiCollapse-wrapperInner': {
      maxHeight: 'none !important',
      minHeight: '0 !important'
    },
    '& .MuiCollapse-entered': {
      maxHeight: 'none !important',
      height: 'auto !important'
    },
    '& .MuiAccordionDetails-root': {
      maxHeight: 'none !important',
      minHeight: '0 !important'
    },
    ...accordionSx
  }), [accordionSx]);

  // Default details styles with inset/depth effect
  const defaultDetailsSx: SxProps<Theme> = useMemo(() => ({
    pt: 3,
    pb: 3,
    px: 3,
    maxHeight: 'none',
    overflow: 'visible',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    borderTop: '2px solid rgba(0, 0, 0, 0.4)',
    borderLeft: '1px solid rgba(0, 0, 0, 0.3)',
    borderRight: '1px solid rgba(255, 255, 255, 0.05)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    borderRadius: '4px',
    boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.3), inset 0 -1px 2px rgba(255, 255, 255, 0.05)',
    ...detailsSx
  }), [detailsSx]);

  return (
    <ArrayInput source={source} label={label}>
      <SimpleFormIterator 
        disableReordering={disableReordering}
        disableAdd={disableAdd}
        disableRemove={disableRemove}
      >
        <FormDataConsumer>
          {({ formData, scopedFormData, getSource }) => {
            // Generate unique ID for this item
            const itemId = getItemId 
              ? getItemId(scopedFormData, formData?.[source]?.indexOf(scopedFormData) || 0)
              : scopedFormData?.id || `item-${Date.now()}-${Math.random()}`;
            
            const isExpanded = expandedAccordions.has(itemId);
            const itemIndex = formData?.[source]?.indexOf(scopedFormData) || 0;

            return (
              <Accordion
                expanded={isExpanded}
                onChange={() => toggleAccordion(itemId)}
                sx={defaultAccordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  {renderSummary(scopedFormData, itemIndex, formData)}
                </AccordionSummary>
                
                <AccordionDetails sx={defaultDetailsSx}>
                  {/* Lazy render: Only render form fields when accordion is expanded */}
                  {isExpanded && renderDetails(scopedFormData, itemIndex, formData, getSource)}
                </AccordionDetails>
              </Accordion>
            );
          }}
        </FormDataConsumer>
      </SimpleFormIterator>
    </ArrayInput>
  );
};

export default LazyAccordionIterator;

